/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { ShoppingCartContext, ShoppingCartItem } from '../models/acdl-models';
import { CartModel, Item } from '../models/cart-model';

type TransformShoppingCartParams = {
  cart: CartModel;
  locale?: string;
};

export function transformShoppingCart(
  params: TransformShoppingCartParams
): ShoppingCartContext {
  const { cart, locale = 'en-US' } = params;

  return {
    id: cart.id,
    items: transformItems(cart.items, locale),
    prices: {
      subtotalExcludingTax: cart.subtotal.excludingTax,
      subtotalIncludingTax: cart.subtotal.includingTax,
    },
    totalQuantity: cart.totalUniqueItems,
    possibleOnepageCheckout: undefined,
    giftMessageSelected: undefined,
    giftWrappingSelected: undefined,
    source: undefined,
  };
}

export function transformItems(
  items: Item[],
  locale: string
): ShoppingCartItem[] {
  return items.map((item: any) => ({
    canApplyMsrp: false,
    formattedPrice: formatCurrency(
      locale,
      item.price.currency,
      item.price.value
    ),
    id: item.uid,
    prices: {
      price: item.price,
      discount: item.discount && item.discount.value !== undefined ? {
        value: item.discount.value,
        currency: item.discount.currency
      } : undefined
    },
    product: {
      productId: item.uid,
      name: item.name,
      sku: item.sku,
      topLevelSku: item.topLevelSku,
      specialToDate: undefined,
      specialFromDate: undefined,
      newToDate: undefined,
      newFromDate: undefined,
      createdAt: undefined,
      updatedAt: undefined,
      manufacturer: undefined,
      countryOfManufacture: undefined,
      categories: item.categories,
      productType: item.itemType,
      pricing: {
        regularPrice: item.regularPrice.value,
        minimalPrice: undefined,
        maximalPrice: undefined,
        specialPrice: transformSpecialPrice(item),
        tierPricing: undefined,
        currencyCode: item.regularPrice.currency,
      },
      canonicalUrl: item.canonicalUrl,
      mainImageUrl: item.image.src,
      image: {
        src: item.image.src,
        alt: item.image.alt,
      },
    },
    configurableOptions: item.selectedOptions ? 
      Object.entries(item.selectedOptions).map(([optionLabel, valueLabel], index) => ({
        id: index + 1,
        optionLabel,
        valueId: index + 1,
        valueLabel: valueLabel as string,
      })) : [],
    bundleOptions: item.bundleOptions ? 
      Object.entries(item.bundleOptions).map(([optionLabel, valueLabel], index) => ({
        id: (index + 1).toString(),
        optionLabel,
        valueId: index + 1,
        valueLabel: valueLabel as string,
      })) : [],
    customizableOptions: item.customizableOptions ? 
      Object.entries(item.customizableOptions).map(([optionLabel, valueLabel], index) => ({
        id: (index + 1).toString(),
        optionLabel,
        valueId: index + 1,
        valueLabel: valueLabel as string,
      })) : [],
    quantity: item.quantity,
    selectedOptions: (() => {
      const options = {
        ...item.selectedOptions,
        ...item.bundleOptions,
        ...item.customizableOptions,
      };
      return Object.keys(options).length > 0 ? options : undefined;
    })(),
  }));
}

function formatCurrency(
  locale: string,
  currency: string,
  value: number
): string {
  const formattedLocale = locale.replace('_', '-');

  return new Intl.NumberFormat(formattedLocale, {
    style: 'currency',
    currency,
  }).format(value);
}

function transformSpecialPrice(item: any) {
  if (!item.discounted) {
    return undefined;
  }
  return item.price?.value;
}
